import React from "react";
import { Box, Typography, TextField, Button } from "@material-ui/core";
import { css, cx } from "@emotion/css";
import { useAiLauncher } from "./AiLauncherContext";
import { contextStarterPrompt } from "./context";

type Message = { role: "user" | "assistant"; text: string };

const STUB_REPLY =
  "🤖 The assistant isn't connected to a backend yet. Once it is, I'll carry out this request against the draft " +
  "(non-published) forms — going through the same validation as manual edits — and you'll review the changes before a " +
  "human publishes them.";

const AssistantPanel: React.VFC = () => {
  const { pending } = useAiLauncher();
  const [input, setInput] = React.useState("");
  const [messages, setMessages] = React.useState<Message[]>([]);
  const endRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (pending) setInput(contextStarterPrompt(pending));
  }, [pending]);

  const scrollToEnd = () => setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);

  const send = (text: string) => {
    if (!text.trim()) return;
    setMessages(prev => [...prev, { role: "user", text }, { role: "assistant", text: STUB_REPLY }]);
    setInput("");
    scrollToEnd();
  };

  return (
    <div className={styles.container}>
      <Box className={styles.transcript}>
        {messages.length === 0 ? (
          <Box className={styles.empty}>
            <Typography variant="h5" style={{ opacity: 0.5 }}>
              🤖 Health Forms Assistant
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center">
              Describe a change to the draft forms — e.g. “add GB translations for every question on this page”, or
              “show this part only when the patient answered yes earlier”.
            </Typography>
          </Box>
        ) : (
          messages.map((msg, i) => (
            <Box key={i} className={cx(styles.bubble, msg.role === "user" ? styles.user : styles.assistant)}>
              <Typography variant="body2">{msg.text}</Typography>
            </Box>
          ))
        )}
        <div ref={endRef} />
      </Box>

      <Box className={styles.inputBar}>
        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={6}
          size="small"
          variant="outlined"
          placeholder="Ask the assistant to change the draft forms..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey && input.trim()) {
              e.preventDefault();
              send(input.trim());
            }
          }}
        />
        <Button variant="contained" color="primary" disabled={!input.trim()} onClick={() => send(input.trim())}>
          Send
        </Button>
      </Box>
    </div>
  );
};

export default AssistantPanel;

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    height: 100%;
  `,
  transcript: css`
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  `,
  empty: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    text-align: center;
    padding: 0 32px;
  `,
  bubble: css`
    padding: 8px 12px;
    border-radius: 8px;
    max-width: 80%;
    word-break: break-word;
  `,
  user: css`
    align-self: flex-end;
    background: #1976d2;
    color: #fff;
  `,
  assistant: css`
    align-self: flex-start;
    background: #f5f5f5;
    color: #333;
  `,
  inputBar: css`
    padding: 16px;
    border-top: 1px solid #e0e0e0;
    display: flex;
    align-items: flex-end;
    gap: 8px;
  `,
};
